import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Compass, Library, Search, Users, Trophy, Target, Coffee, LogOut, Menu, X, Globe, Shield } from "lucide-react";
import { Button } from "./ui/button";
import { UserAvatar } from "./UserAvatar";
import { NotificationBell } from "./NotificationBell";
import { useTheme } from "./ThemeProvider";
import { useTranslation } from "../i18n";
import axios from "axios";
import { toast } from "sonner";

const API = '/api';

const navItemDefs = [
  { icon: Compass, key: "dashboard", path: "/dashboard" },
  { icon: Library, key: "library", path: "/library" },
  { icon: Search, key: "search", path: "/search" },
  { icon: Users, key: "discover", path: "/feed" },
  { icon: Trophy, key: "achievements", path: "/achievements" },
  { icon: Target, key: "challenges", path: "/challenges" },
];

export function MobileNav({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const { theme } = useTheme();
  const { t, lang, setLang } = useTranslation();
  const isDark = theme === "dark" || (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  const horizontalLogo = isDark ? "/logo-horizontal-dark.svg" : "/logo-horizontal.svg";

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) { /* ignore */ }
    localStorage.removeItem("session_token");
    toast.success(t.nav.loggedOut);
    setMenuOpen(false);
    navigate("/");
  };

  const toggleLang = () => { setLang(lang === "es" ? "en" : "es"); };

  return (
    <>
      {/* Top Header Bar */}
      <header
        className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-background/95 backdrop-blur-md border-b border-border z-50 flex items-center px-2"
        data-testid="mobile-nav"
      >
        <Button
          variant="ghost"
          size="icon"
          className="w-9 h-9 text-muted-foreground flex-shrink-0"
          onClick={() => setMenuOpen(true)}
          data-testid="mobile-menu-toggle"
        >
          <Menu className="w-5 h-5" strokeWidth={1.8} />
        </Button>

        <nav className="flex items-center gap-0.5 flex-1 justify-center overflow-x-auto scrollbar-hide" data-testid="mobile-quick-nav">
          {navItemDefs.slice(1, 5).map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                title={t.nav[item.key] || item.key}
                data-testid={`mobile-qnav-${item.path.slice(1)}`}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors flex-shrink-0 ${
                  isActive
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                <item.icon className="w-[18px] h-[18px]" strokeWidth={isActive ? 2.2 : 1.8} />
              </Link>
            );
          })}
          <a
            href="https://ko-fi.com/adventuretrack"
            target="_blank"
            rel="noopener noreferrer"
            title="Ko-fi"
            className="w-9 h-9 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex-shrink-0"
            data-testid="mobile-kofi"
          >
            <Coffee className="w-[18px] h-[18px]" strokeWidth={1.8} />
          </a>
          {user && <NotificationBell />}
        </nav>

        <Link to="/dashboard" className="flex items-center flex-shrink-0" data-testid="mobile-logo">
          <img src="/favicon.svg" alt="Adventure Track!" className="w-6 h-6 object-contain" />
        </Link>
      </header>

      {/* Overlay */}
      {menuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-[60] transition-opacity"
          onClick={() => setMenuOpen(false)}
          data-testid="mobile-menu-overlay"
        />
      )}

      {/* Slide-in Drawer */}
      <div
        className={`lg:hidden fixed top-0 left-0 bottom-0 w-72 bg-card z-[70] transform transition-transform duration-300 ease-in-out flex flex-col ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="mobile-menu-drawer"
      >
        <div className="flex items-center justify-between p-4 pb-2">
          <Link to="/dashboard" className="flex items-center gap-2.5" onClick={() => setMenuOpen(false)}>
            <img src={horizontalLogo} alt="AdventureTrack!" className="h-6 object-contain" />
          </Link>
          <Button variant="ghost" size="icon" className="w-8 h-8 text-muted-foreground" onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
          {navItemDefs.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`mobile-nav-${item.path.slice(1)}`}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <item.icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={isActive ? 2.2 : 1.8} />
                <span className="text-sm">{t.nav[item.key] || item.key}</span>
              </Link>
            );
          })}

          <a
            href="https://ko-fi.com/adventuretrack"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-kofi-nav"
            onClick={() => setMenuOpen(false)}
            className="flex items-center gap-3 px-3 py-3 rounded-lg transition-colors text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Coffee className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.8} />
            <span className="text-sm">Ko-fi</span>
          </a>

          {/* Admin link (only for admins) */}
          {user?.role === "admin" && (
            <Link
              to="/admin"
              data-testid="mobile-nav-admin"
              onClick={() => setMenuOpen(false)}
              className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                location.pathname === "/admin" ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              <Shield className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.8} />
              <span className="text-sm">Admin</span>
            </Link>
          )}
        </nav>

        <div className="px-3 pb-4 pt-2 border-t border-border space-y-1">
          {/* Language toggle */}
          <button
            onClick={toggleLang}
            data-testid="mobile-lang-toggle"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Globe className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.8} />
            <span className="text-sm">{lang === "es" ? "English" : "Español"}</span>
          </button>

          {user && (
            <Link
              to={user.username ? `/profile/${user.username}` : "/profile"}
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors"
            >
              <UserAvatar user={user} size="md" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate text-foreground">{user.name?.split(" ")[0] || user.username}</p>
                <p className="text-xs text-muted-foreground truncate">@{user.username || "user"}</p>
              </div>
            </Link>
          )}

          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="mobile-logout-button"
            className="w-full justify-start gap-3 px-3 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
          >
            <LogOut className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.8} />
            <span className="text-sm">{t.nav.logout}</span>
          </Button>
        </div>
      </div>
    </>
  );
}
